import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createManualTaxLot,
  getManualTransaction,
  resolveInboxItemCombined,
  updateManualTaxLot,
  getAllocatedQuantity,
  ManualTaxLot,
  ManualTaxLotBody,
  LotAction,
  MissingCostBasisItem,
  UnassignedDisposalResolution,
} from '../../api/tax';
import { useEscToClose } from '../../hooks/useEscToClose';
import InboxRowContext from './InboxRowContext';
import '../CancelTradeGroupModal.css';

interface ManualTaxLotModalProps {
  mode: 'create' | 'edit';
  initial?: ManualTaxLot;
  // Pre-fill from inbox (TASK-019 will use these)
  portfolioId?: string;
  disposalOrderId?: string;
  /**
   * Inbox row context (TASK-015 / TASK-016). When `source === 'inbox'` the
   * modal renders the inbox context block, hides the lot-type selector and
   * the numeric transaction-id fields, and submits via the combined inbox-
   * resolution endpoint instead of the standalone create endpoint.
   */
  inboxItem?: MissingCostBasisItem;
  /**
   * Drives the inbox-mode rendering & submit path (TASK-016). Defaults to
   * `'crud'` so existing call sites that open the modal from the manual
   * tax-lot CRUD view are unchanged.
   */
  source?: 'inbox' | 'crud';
  onClose: () => void;
  onSuccess: () => void;
}

type LotType = 'CREATE_MANUAL_LOT' | 'LINK_COINBASE_TO_MANUAL';

const ManualTaxLotModal: React.FC<ManualTaxLotModalProps> = ({
  mode,
  initial,
  portfolioId: initialPortfolioId,
  disposalOrderId: initialDisposalOrderId,
  inboxItem,
  source = 'crud',
  onClose,
  onSuccess,
}) => {
  const queryClient = useQueryClient();

  useEscToClose(onClose);

  const isInbox = source === 'inbox' && !!inboxItem;

  // Form state
  const [lotType, setLotType] = useState<LotType>('CREATE_MANUAL_LOT');
  const [portfolioId, setPortfolioId] = useState<string>(initialPortfolioId ?? initial?.portfolio_id ?? '');
  const [acquisitionTransactionId, setAcquisitionTransactionId] = useState<string>('');
  const [disposalTransactionId, setDisposalTransactionId] = useState<string>('');
  const [coinbaseOrderId, setCoinbaseOrderId] = useState<string>('');
  const [manualTransactionId, setManualTransactionId] = useState<string>('');
  // For pre-fill from UNASSIGNED_DISPOSAL the disposal side comes from a
  // Coinbase order ID that needs a manual acquisition. Surface it in the
  // allocation query even though the create body uses a different field name.
  const [acquisitionOrderId] = useState<string>('');
  const [disposalOrderId] = useState<string>(initialDisposalOrderId ?? '');
  const [quantity, setQuantity] = useState<string>(
    isInbox ? (inboxItem?.quantity ?? '') : (initial?.quantity ?? '')
  );
  const [reason, setReason] = useState<string>('');

  // Inbox-mode-only inputs (TASK-016).
  // Default acquisition date to the disposal's occurrence date minus 1 day —
  // the operator must have acquired the asset before disposing of it. UTC math
  // to keep the calendar date stable regardless of the browser's local tz.
  const [acquisitionCostUsd, setAcquisitionCostUsd] = useState<string>('');
  const [acquisitionDate, setAcquisitionDate] = useState<string>(() => {
    if (!isInbox || !inboxItem?.occurrence_date) return '';
    const datePart = inboxItem.occurrence_date.slice(0, 10);
    const [y, m, d] = datePart.split('-').map(Number);
    if (!y || !m || !d) return '';
    const dayBefore = new Date(Date.UTC(y, m - 1, d - 1));
    return dayBefore.toISOString().slice(0, 10);
  });

  const [serverError, setServerError] = useState<string | null>(null);

  // Real-time allocation: for CREATE_MANUAL_LOT the "order IDs" in the
  // allocation endpoint are the manual transaction IDs (stored as strings in
  // tax_lot.acquisition_order_id / disposal_order_id). For LINK_COINBASE_TO_MANUAL
  // use the Coinbase order ID. For inbox pre-fill use the passed-in disposal order ID.
  const allocationAcqId = lotType === 'CREATE_MANUAL_LOT'
    ? (acquisitionTransactionId.trim() || acquisitionOrderId || undefined)
    : (acquisitionOrderId || undefined);
  const allocationDispId = lotType === 'CREATE_MANUAL_LOT'
    ? (disposalTransactionId.trim() || disposalOrderId || undefined)
    : (coinbaseOrderId.trim() || disposalOrderId || undefined);
  const allocationEnabled = !isInbox && !!(portfolioId && (allocationAcqId || allocationDispId));

  const allocationQuery = useQuery({
    queryKey: ['tax', 'allocation', portfolioId, allocationAcqId, allocationDispId],
    queryFn: () => getAllocatedQuantity(portfolioId, allocationAcqId, allocationDispId),
    enabled: allocationEnabled,
  });

  const allocatedQuantity = allocationQuery.data?.data.allocatedQuantity;

  // Fetch the acquisition transaction to learn its total quantity so we can
  // compute the available remainder and disable submit on over-allocation.
  const acqTxId = !isInbox && lotType === 'CREATE_MANUAL_LOT' && acquisitionTransactionId.trim()
    ? Number(acquisitionTransactionId.trim())
    : NaN;
  const acqTxQuery = useQuery({
    queryKey: ['tax', 'manual-transaction', acqTxId],
    queryFn: () => getManualTransaction(acqTxId),
    enabled: !isNaN(acqTxId),
  });
  const acqTxQuantity = acqTxQuery.data?.data.quantity;

  const isOverAllocated = useMemo(() => {
    if (isInbox) return false;
    if (mode === 'edit') return false;
    if (!allocatedQuantity || !quantity.trim() || !acqTxQuantity) return false;
    const proposed = parseFloat(quantity);
    const allocated = parseFloat(allocatedQuantity);
    const total = parseFloat(acqTxQuantity);
    if (isNaN(proposed) || isNaN(allocated) || isNaN(total) || total <= 0) return false;
    return (proposed + allocated) > total;
  }, [isInbox, mode, quantity, allocatedQuantity, acqTxQuantity]);

  // Determine lotAction from the chosen lot type. The user picks the high-
  // level intent; the body shape follows. For LINK_COINBASE_TO_MANUAL we use
  // the Coinbase ID as the acquisition; the manual transaction is the
  // disposal (or vice-versa). For the MVP we expose the simpler form.
  const lotAction: LotAction = lotType;

  const isFormValid = useMemo(() => {
    if (mode === 'edit') {
      return quantity.trim() !== '' && reason.trim() !== '';
    }
    if (isInbox) {
      return (
        acquisitionCostUsd.trim() !== '' &&
        acquisitionDate.trim() !== '' &&
        reason.trim() !== ''
      );
    }
    if (!portfolioId.trim() || !quantity.trim() || !reason.trim()) return false;
    if (isOverAllocated) return false;
    if (lotType === 'CREATE_MANUAL_LOT') {
      return acquisitionTransactionId.trim() !== '' && disposalTransactionId.trim() !== '';
    }
    return coinbaseOrderId.trim() !== '' && manualTransactionId.trim() !== '';
  }, [
    mode,
    isInbox,
    acquisitionCostUsd,
    acquisitionDate,
    portfolioId,
    quantity,
    reason,
    isOverAllocated,
    lotType,
    acquisitionTransactionId,
    disposalTransactionId,
    coinbaseOrderId,
    manualTransactionId,
  ]);

  const createMutation = useMutation({
    mutationFn: (body: ManualTaxLotBody) => createManualTaxLot(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tax', 'manual-tax-lots'] });
      onSuccess();
      onClose();
    },
    onError: (err) => {
      setServerError(err instanceof Error ? err.message : 'Failed to create manual tax lot');
    },
  });

  const updateMutation = useMutation({
    mutationFn: () =>
      updateManualTaxLot(initial!.lot_id, {
        quantity: quantity.trim(),
        reason: reason.trim(),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tax', 'manual-tax-lots'] });
      onSuccess();
      onClose();
    },
    onError: (err) => {
      setServerError(err instanceof Error ? err.message : 'Failed to update manual tax lot');
    },
  });

  // Inbox-mode submit: call the combined endpoint with
  // resolutionType=MANUAL_TAX_LOT_CREATED. Preserves CTR-013's invalidation
  // of the missingCostBasis query so the inbox refreshes on success.
  const inboxResolveMutation = useMutation({
    mutationFn: (body: UnassignedDisposalResolution) => resolveInboxItemCombined(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tax', 'manual-tax-lots'] });
      queryClient.invalidateQueries({ queryKey: ['tax', 'manual-transactions'] });
      queryClient.invalidateQueries({ queryKey: ['missingCostBasis'] });
      onSuccess();
      onClose();
    },
    onError: (err) => {
      setServerError(err instanceof Error ? err.message : 'Failed to resolve inbox item');
    },
  });

  const isPending =
    createMutation.isPending || updateMutation.isPending || inboxResolveMutation.isPending;

  const handleSubmit = () => {
    setServerError(null);
    if (mode === 'edit') {
      updateMutation.mutate();
      return;
    }
    if (isInbox && inboxItem) {
      // Operator enters total acquisition cost (USD); the backend takes
      // pricePerUnit (USD per unit). Derive client-side so the natural UX
      // (total cost) maps to the manual_transaction contract. The backend
      // rounds pricePerUnit to 2 dp at the API boundary — for disposals
      // where quantity does not divide the total evenly, the persisted
      // total_value can differ from the operator's input by up to 1¢,
      // which is within IRS Form 8949 dollar-rounding tolerance.
      //
      // Deliberate deviation from the project-wide BigDecimal-for-money
      // rule: JS has no BigDecimal primitive. The division below uses
      // double arithmetic, and that is acceptable here because the backend
      // re-rounds to 2 dp at the API boundary (REQ-015) — the operator's
      // truth is the total cost they typed, and pricePerUnit is a derived
      // wire value, not a stored truth. Do not copy this pattern to any
      // place where the divided value is itself the persisted amount.
      const qty = inboxItem.quantity;
      const totalCost = parseFloat(acquisitionCostUsd.trim());
      const qtyNum = parseFloat(qty);
      const pricePerUnit = qtyNum > 0 ? (totalCost / qtyNum).toFixed(8) : '0';
      inboxResolveMutation.mutate({
        portfolioId: inboxItem.portfolio_id,
        sourceIdentifier: inboxItem.source_order_id,
        itemType: 'UNASSIGNED_DISPOSAL',
        resolutionType: 'MANUAL_TAX_LOT_CREATED',
        productId: inboxItem.product_id,
        transactionDate: `${acquisitionDate.trim()}T00:00:00Z`,
        quantity: qty,
        pricePerUnit,
        reason: reason.trim(),
        resolvedQuantity: qty,
      });
      return;
    }
    const body: ManualTaxLotBody = {
      lotAction,
      portfolioId: portfolioId.trim(),
      quantity: quantity.trim(),
      reason: reason.trim(),
    };
    if (lotType === 'CREATE_MANUAL_LOT') {
      body.acquisitionTransactionId = Number(acquisitionTransactionId.trim());
      body.disposalTransactionId = Number(disposalTransactionId.trim());
    } else {
      body.coinbaseOrderId = coinbaseOrderId.trim();
      body.manualTransactionId = Number(manualTransactionId.trim());
    }
    createMutation.mutate(body);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content cancel-trade-group-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2>{mode === 'create' ? 'New Manual Tax Lot' : 'Edit Manual Tax Lot'}</h2>
          <button
            className="close-button"
            onClick={onClose}
            type="button"
            aria-label="Close manual tax lot modal"
          >
            ×
          </button>
        </div>

        <div className="modal-body">
          {isInbox && inboxItem && <InboxRowContext item={inboxItem} />}

          {mode === 'create' && !isInbox && (
            <div className="form-group">
              <label htmlFor="mtl-lot-type">Lot Type</label>
              <select
                id="mtl-lot-type"
                className="form-select"
                value={lotType}
                onChange={(e) => setLotType(e.target.value as LotType)}
              >
                <option value="CREATE_MANUAL_LOT">Create Manual Lot</option>
                <option value="LINK_COINBASE_TO_MANUAL">Link Coinbase to Manual</option>
              </select>
            </div>
          )}

          {!isInbox && (
            <div className="form-group">
              <label htmlFor="mtl-portfolio">Portfolio ID</label>
              <input
                id="mtl-portfolio"
                type="text"
                className="form-input"
                value={portfolioId}
                onChange={(e) => setPortfolioId(e.target.value)}
                disabled={mode === 'edit'}
              />
            </div>
          )}

          {mode === 'create' && !isInbox && lotType === 'CREATE_MANUAL_LOT' && (
            <>
              <div className="form-group">
                <label htmlFor="mtl-acq-tx">Acquisition Manual Transaction ID</label>
                <input
                  id="mtl-acq-tx"
                  type="number"
                  className="form-input"
                  value={acquisitionTransactionId}
                  onChange={(e) => setAcquisitionTransactionId(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label htmlFor="mtl-disp-tx">Disposal Manual Transaction ID</label>
                <input
                  id="mtl-disp-tx"
                  type="number"
                  className="form-input"
                  value={disposalTransactionId}
                  onChange={(e) => setDisposalTransactionId(e.target.value)}
                />
              </div>
            </>
          )}

          {mode === 'create' && !isInbox && lotType === 'LINK_COINBASE_TO_MANUAL' && (
            <>
              <div className="form-group">
                <label htmlFor="mtl-cb-order">Coinbase Order ID</label>
                <input
                  id="mtl-cb-order"
                  type="text"
                  className="form-input"
                  value={coinbaseOrderId}
                  onChange={(e) => setCoinbaseOrderId(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label htmlFor="mtl-manual-tx">Manual Transaction ID</label>
                <input
                  id="mtl-manual-tx"
                  type="number"
                  className="form-input"
                  value={manualTransactionId}
                  onChange={(e) => setManualTransactionId(e.target.value)}
                />
              </div>
            </>
          )}

          {isInbox && (
            <>
              <div className="form-group">
                <label htmlFor="mtl-acq-cost" className="required">Acquisition Cost (USD)</label>
                <input
                  id="mtl-acq-cost"
                  type="number"
                  step="0.01"
                  className="form-input"
                  value={acquisitionCostUsd}
                  onChange={(e) => setAcquisitionCostUsd(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="mtl-acq-date" className="required">Acquisition Date</label>
                <input
                  id="mtl-acq-date"
                  type="date"
                  className="form-input"
                  value={acquisitionDate}
                  onChange={(e) => setAcquisitionDate(e.target.value)}
                  required
                />
              </div>
            </>
          )}

          {!isInbox && (
            <div className="form-group">
              <label htmlFor="mtl-quantity">Quantity</label>
              <input
                id="mtl-quantity"
                type="text"
                className="form-input"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
              />
              {allocationEnabled && (
                <small className={`form-hint${isOverAllocated ? ' form-hint--error' : ''}`}>
                  {allocationQuery.isLoading
                    ? 'Loading allocation…'
                    : allocationQuery.isError
                      ? 'Failed to load allocation'
                      : allocatedQuantity !== undefined
                        ? `Already allocated: ${allocatedQuantity}${acqTxQuantity ? ` of ${acqTxQuantity}` : ''}`
                        : null}
                </small>
              )}
              {isOverAllocated && (
                <div className="delete-warning-box" role="alert">
                  <span className="delete-warning-text">
                    Proposed quantity would exceed the available allocation.
                  </span>
                </div>
              )}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="mtl-reason" className={isInbox ? 'required' : undefined}>Reason</label>
            <input
              id="mtl-reason"
              type="text"
              className="form-input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required={isInbox}
            />
          </div>

          {serverError && (
            <div className="delete-warning-box" role="alert">
              <strong className="delete-warning-title">Error:</strong>
              <span className="delete-warning-text">{serverError}</span>
            </div>
          )}

          {!isFormValid && !isPending && (
            <small className="form-hint">
              {isInbox
                ? 'All three fields (acquisition cost, date, reason) are required.'
                : mode === 'edit'
                  ? 'Quantity and reason are required.'
                  : 'Fill in all required fields to enable Create.'}
            </small>
          )}
        </div>

        <div className="modal-footer">
          <button
            type="button"
            className="modal-pill-button modal-pill-cancel"
            onClick={onClose}
            disabled={isPending}
          >
            Cancel
          </button>
          <button
            type="button"
            className="modal-pill-button modal-pill-primary"
            onClick={handleSubmit}
            disabled={!isFormValid || isPending}
          >
            {isPending ? 'Submitting…' : mode === 'create' ? 'Create' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ManualTaxLotModal;
